import React from "react";
import { Text } from "@components/core/Text";
import { Banner } from "./Banner";
import {
  getCurrentHolidayPromoConfig,
  getHolidayPromoForcedPlanPrice,
} from "src/utils/holidayPromo";
import { isTaxExclusiveCountry } from "src/utils/userSegment/features";
import i18n from "src/locales";
import {
  PROMO_TYPE,
  PROMO_PRODUCT_TYPE,
  PercentOffPromoConfig,
  FlatAmountOffPromoConfig,
} from "src/utils/holidayPromoConstants";

function getOfferTermsTranslationKey(
  productTypeTranslationKey: string,
  promoTypeTranslationKey: string,
  section: string,
  campaign?: string
) {
  if (productTypeTranslationKey === PROMO_PRODUCT_TYPE.NOOM_MED) {
    const promoKey = campaign
      ? `${promoTypeTranslationKey}${campaign}`
      : promoTypeTranslationKey;
    return `holidayPromotion:offerTerms:${productTypeTranslationKey}:${section}:${promoKey}`;
  }

  return `holidayPromotion:offerTerms:${productTypeTranslationKey}:${section}`;
}

function HolidayPromoModalContent() {
  const promoConfig = getCurrentHolidayPromoConfig();

  let promoTypeTranslationKey;
  if (promoConfig.promoType === PROMO_TYPE.MONTHS_OFF) {
    promoTypeTranslationKey = "monthsOff";
  } else if (promoConfig.promoType === PROMO_TYPE.FLAT_AMOUNT_OFF) {
    promoTypeTranslationKey = "flatAmountOff";
  } else {
    promoTypeTranslationKey = "percentageOff";
  }

  const taxTypeTranslationKey = isTaxExclusiveCountry()
    ? "taxExclusive"
    : "taxInclusive";
  const { productTypeTranslationKey, campaign } = promoConfig;

  const { percentageOff, flatAmountOff } =
    promoConfig as PercentOffPromoConfig & FlatAmountOffPromoConfig;

  const planPrice = getHolidayPromoForcedPlanPrice();

  return (
    <>
      <Text i18nKey="holidayPromotion:offerTerms:title" />
      <ul>
        <li>
          <Text
            i18nKey={getOfferTermsTranslationKey(
              productTypeTranslationKey,
              promoTypeTranslationKey,
              "offerTerms",
              campaign
            )}
          />
        </li>
        <li>
          <Text
            i18nKey={`holidayPromotion:offerTerms:${productTypeTranslationKey}:promoCodeTerms:${promoTypeTranslationKey}:${taxTypeTranslationKey}`}
            values={{
              promoCode: promoConfig.uiPromoCode,
              percentageOff,
              flatAmountOff,
            }}
          />
        </li>
        <li>
          <Text
            i18nKey={getOfferTermsTranslationKey(
              productTypeTranslationKey,
              promoTypeTranslationKey,
              "requirements",
              campaign
            )}
          />
        </li>
        {productTypeTranslationKey === PROMO_PRODUCT_TYPE.NOOM_MED && (
          <li>
            <Text
              i18nKey={getOfferTermsTranslationKey(
                productTypeTranslationKey,
                promoTypeTranslationKey,
                "limitations",
                campaign
              )}
            />
          </li>
        )}
      </ul>
      <Text i18nKey="holidayPromotion:subscriptionTerms:title" />
      <ul>
        <li>{i18n.t("holidayPromotion:subscriptionTerms:line1")}</li>
        <li>
          <Text
            i18nKey={`holidayPromotion:subscriptionTerms:${productTypeTranslationKey}:line2:${promoTypeTranslationKey}:${taxTypeTranslationKey}`}
            values={{
              planPrice,
              percentageOff,
              flatAmountOff,
            }}
          />
        </li>
        <li>
          <Text
            i18nKey={`holidayPromotion:subscriptionTerms:${productTypeTranslationKey}:line3:${promoTypeTranslationKey}:${taxTypeTranslationKey}`}
            values={{
              planPrice,
              percentageOff,
              flatAmountOff,
            }}
          />
        </li>
        <li>{i18n.t("holidayPromotion:subscriptionTerms:line4")}</li>
        <li>{i18n.t("holidayPromotion:subscriptionTerms:line5")}</li>
      </ul>
    </>
  );
}

export function HolidayPromoBanner() {
  const promoConfig = getCurrentHolidayPromoConfig();

  return (
    <Banner
      fullWidth
      headline={`holidayPromotion:${promoConfig.translationKey}:headline`}
      headlineValues={{
        promoCode: promoConfig.uiPromoCode,
      }}
      modalContent={<HolidayPromoModalContent />}
    />
  );
}
